import { useQuery } from '@apollo/client';
import { useRef, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';

import { ORDERS_BY_QUERY } from 'app/graphql/cart-api';
import { OrderStatus, Marketplace, OrdersByQueryQuery } from 'app/graphql/generated/cart/graphql';

type OrderNode = NonNullable<OrdersByQueryQuery['ordersByQuery']['edges']>[number]['node'];
export type OrderTableShipment = OrderNode['shipments'][number];
export type OrderTableLineItem = OrderNode['lineItems'][number];
import { getStatusLabel, getStatusLabelColor } from 'utils/graphqlOrderStatus';
import { getFormattedCurrency } from 'utils/formatters';
import { Routes } from 'config/constants';
import { env } from 'config/env';
import { useFlags } from 'providers/FlagsProvider';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { DataTable } from 'components/ui/data-table';
import { Skeleton } from 'components/ui/skeleton';
import { Button } from 'components/ui/button';
import { CopyId } from 'components/ui/copy-id';
import { Timestamp } from 'components/ui/timestamp';
import { DataTablePaginationState } from 'components/ui/data-table-pagination';
import { TrackingInfo } from 'components/Orders/Tracking/TrackingInfo';
import { TestWebhookSender } from 'components/Orders/OrderTable/testWebhookSender';
import PreloaderIcon, { RyeLogoSizeClass } from 'components/Preloader';

import { ReactComponent as EmptyBox } from 'assets/icons/empty-box.svg';
import { EmptyState } from 'components/EmptyState';
import { Container } from 'pages/CheckoutIntents/styles';
import { useOrderVendors, OrderItem } from './useOrderVendors';

import 'components/Orders/style.scss';

export interface OrderTableItem {
  id: string;
  status: OrderStatus;
  statusLabel: string;
  marketplace: Marketplace;
  /** Pre-formatted timestamp kept for backwards compat. */
  createdAt: string;
  /** Raw ISO timestamp so the Timestamp tooltip can show LOCAL/UTC/relative. */
  createdAtIso: string;
  subtotal: string | null;
  total: string | null;
  shipments: OrderTableShipment[];
  lineItems: OrderTableLineItem[];
  orderDidPlace: boolean;
}

const baseColumns: ColumnDef<OrderTableItem>[] = [
  {
    accessorKey: 'id',
    header: 'Order Id',
    // Click-to-copy on the ID itself; the row click handler still
    // navigates to the order details. Stop propagation so copying doesn't
    // also fire the row click.
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <CopyId value={row.original.id} />
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created on',
    cell: ({ row }) => <Timestamp date={row.original.createdAtIso} />,
  },
  {
    accessorKey: 'brand',
    header: 'Brand',
    cell: ({ row }) => {
      // This will be populated by BrandCell component
      return <BrandCell orderId={row.original.id} />;
    },
  },
  {
    accessorKey: 'subtotal',
    header: 'Revenue',
  },
  {
    accessorKey: 'total',
    header: 'Total',
  },
  {
    accessorKey: 'tracking',
    header: 'Tracking',
    cell: ({ row }) => <TrackingCell order={row.original} />,
  },
  {
    accessorKey: 'statusLabel',
    header: 'Order Status',
    cell: ({ row }) => {
      const statusMixin = getStatusLabelColor(row.original.status);
      return <div className={`order-center-label ${statusMixin}`}>{row.original.statusLabel}</div>;
    },
  },
];

// Test webhooks column is only available in development/staging
const columns: ColumnDef<OrderTableItem>[] =
  env.VITE_NODE_ENV !== 'production'
    ? [
        ...baseColumns,
        {
          accessorKey: 'testWebhooks',
          header: 'Test webhooks',
          cell: ({ row }) => <TestWebhookCell order={row.original} />,
        },
      ]
    : baseColumns;

const BrandCell = ({ orderId }: { orderId: string }) => {
  const vendors = useOrderVendors([]);
  const vendor = vendors[orderId];
  const isVendorLoading = !vendor || (vendor.loading && !vendor.title);

  if (isVendorLoading) {
    return <PreloaderIcon size={RyeLogoSizeClass.micro} />;
  }

  return <>{vendor?.title || '-'}</>;
};

const TrackingCell = ({ order }: { order: OrderTableItem }) => {
  const { shipments, status } = order;

  if (shipments && shipments.length > 0) {
    const firstShipment = shipments[0];
    return (
      <TrackingInfo
        tracking={{
          trackingNumber: firstShipment.carrierTrackingNumber,
          courierUrl: firstShipment.carrierTrackingUrl ?? undefined,
          courierName: firstShipment.carrierName,
        }}
      />
    );
  }

  // If the order has been cancelled, it doesn't make sense to say the tracking info isn't available yet
  const trackingFallbackMessage = status === OrderStatus.Cancelled ? 'N/A' : 'Not yet available';

  return <>{trackingFallbackMessage}</>;
};

const TestWebhookCell = ({ order }: { order: OrderTableItem }) => {
  const user = useAppSelector(selectUserInfo);
  const webhookUrl = user?.webhookURL;
  const developerId = user?.uid;

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex min-w-max">
      <TestWebhookSender
        checkoutRequestId={order.id}
        developerId={developerId}
        orderDidPlace={order.orderDidPlace}
        webhookUrl={webhookUrl}
      />
    </div>
  );
};

const transformTableData = (
  ordersConnection: OrdersByQueryQuery['ordersByQuery'] | undefined | null,
): OrderTableItem[] => {
  if (!ordersConnection?.edges) {
    return [];
  }

  return ordersConnection.edges.map((edge) => {
    const order = edge.node;
    const currency = order.total?.currency ?? 'USD';

    return {
      id: order.id,
      status: order.status,
      statusLabel: getStatusLabel(order.status),
      marketplace: order.marketplace,
      createdAt: format(new Date(order.createdAt), 'MM/dd/yyyy HH:mm:ss'),
      createdAtIso: order.createdAt,
      subtotal:
        order.subtotal?.value != null
          ? getFormattedCurrency(currency, order.subtotal.value / 100)
          : null,
      total:
        order.total?.value != null ? getFormattedCurrency(currency, order.total.value / 100) : null,
      shipments: order.shipments,
      lineItems: order.lineItems,
      orderDidPlace: order.lineItems.length > 0,
    };
  });
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { viewCreateOrder } = useFlags();
  const [loading, setLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const beforeCursorRef = useRef<string | undefined>();
  const afterCursorRef = useRef<string | undefined>();
  const [tableData, setTableData] = useState<OrderTableItem[]>([]);
  const [pageSize, setPageSize] = useState(10);

  const {
    data,
    fetchMore,
    loading: queryLoading,
  } = useQuery(ORDERS_BY_QUERY, {
    variables: {
      pagination: {
        first: pageSize,
      },
    },
    onCompleted: (data) => {
      const items = transformTableData(data.ordersByQuery);
      setTableData(items);
      afterCursorRef.current = data.ordersByQuery.pageInfo.endCursor ?? undefined;
      beforeCursorRef.current = data.ordersByQuery.pageInfo.startCursor ?? undefined;
      setHasNextPage(data.ordersByQuery.pageInfo.hasNextPage ?? false);
      setHasPreviousPage(data.ordersByQuery.pageInfo.hasPreviousPage ?? false);
      setLoading(false);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error('There was an error fetching orders', {
        position: 'bottom-right',
      });
      setLoading(false);
    },
  });

  // Build items for vendor fetching
  const orderItems: OrderItem[] = useMemo(() => {
    return tableData.map((item) => ({
      id: item.id,
      lineItems: item.lineItems,
      marketplace: item.marketplace,
    }));
  }, [tableData]);

  // Fetch vendors for current page items
  useOrderVendors(orderItems);

  const handlePaginationChange = async ({
    pageSize: newPageSize,
    direction,
  }: DataTablePaginationState) => {
    setLoading(true);

    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }

    try {
      const { data } = await fetchMore({
        variables: {
          pagination: {
            first: direction === 'next' || direction === undefined ? newPageSize : undefined,
            last: direction === 'previous' ? newPageSize : undefined,
            after: direction === 'next' ? afterCursorRef.current : undefined,
            before: direction === 'previous' ? beforeCursorRef.current : undefined,
          },
        },
      });

      afterCursorRef.current = data.ordersByQuery.pageInfo.endCursor ?? undefined;
      beforeCursorRef.current = data.ordersByQuery.pageInfo.startCursor ?? undefined;

      setTableData(transformTableData(data.ordersByQuery));
      setHasNextPage(data.ordersByQuery.pageInfo.hasNextPage || false);
      setHasPreviousPage(data.ordersByQuery.pageInfo.hasPreviousPage || false);
    } catch (e) {
      console.error('Error fetching more orders:', e);
      Sentry.captureException(new Error('Error fetching more orders', { cause: e }));
      toast.error('There was an error fetching more orders', {
        position: 'bottom-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (row: OrderTableItem) => {
    navigate(`${Routes.Orders}/${row.id}`);
  };

  // Show loading skeleton initially
  if (queryLoading && !data) {
    return (
      <Container>
        <Card className="mt-[28px]">
          <CardHeader className="flex-auto flex-row justify-between">
            <div className="space-y-1">
              <Skeleton className="h-4 w-[100px]" />
              <CardDescription>List of orders created through the Rye API</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card className="mt-[28px]">
        <CardHeader className="flex-auto flex-row justify-between">
          <div className="space-y-1">
            {loading || queryLoading ? (
              <Skeleton className="h-4 w-[100px]" />
            ) : (
              <CardTitle className="whitespace-break-spaces">Your Orders</CardTitle>
            )}
            <CardDescription>List of orders created through the Rye API</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {viewCreateOrder && (
              <Button asChild>
                <Link to={Routes.CreateOrder}>Create order</Link>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            loading={loading || queryLoading}
            columns={columns}
            data={tableData}
            onRowClicked={handleRowClick}
            pageSize={pageSize}
            onPaginationChange={handlePaginationChange}
            emptyState={<EmptyState icon={<EmptyBox />} title="No Orders Yet" />}
            manualPagination={true}
            hideTotalPageCount={true}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            hideToolbar={true}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default OrdersPage;
